import { isAffirmative } from "@/utils/isAffirmative/isAffirmative";

export type EapServiceKey =
  | "mentalWellbeing"
  | "financialAssistance"
  | "lifeCoaching"
  | "careerCounselling"
  | "consumerAdviser"
  | "mediation"
  | "nonNationals"
  | "legalAssistance"
  | "parentalAdvice"
  | "menopauseSupport";

export type EapService = {
  key: EapServiceKey | null;
  title: string;
  icon: string;
};

type EapServicesOffered = {
  active?: unknown;
  items?: unknown;
};

type EapServiceItem = {
  title?: unknown;
  active?: unknown;
  img_url?: unknown;
};

// Core translates titles for non-English members, so the img_url slug is the stable key
// (core-api ClientPageCustomisations::$default_eap). Titles are the fallback for configs
// saved with a different image path.
const SERVICE_KEY_BY_IMAGE_SLUG: Record<string, EapServiceKey> = {
  "mental-wellbeing-support": "mentalWellbeing",
  "financial-assistance": "financialAssistance",
  "life-coaching": "lifeCoaching",
  "career-counselling": "careerCounselling",
  "consumer-advisor": "consumerAdviser",
  mediation: "mediation",
  "support-non-irish-national": "nonNationals",
  "legal-assistance": "legalAssistance",
  "parental-advice": "parentalAdvice",
  "menopause-support": "menopauseSupport",
};

const SERVICE_KEY_BY_TITLE: Record<string, EapServiceKey> = {
  "mental wellbeing support": "mentalWellbeing",
  "financial assistance": "financialAssistance",
  "life coaching": "lifeCoaching",
  "career counselling": "careerCounselling",
  "consumer adviser": "consumerAdviser",
  mediation: "mediation",
  "support for non-nationals & their families": "nonNationals",
  "legal assistance": "legalAssistance",
  "parental advice": "parentalAdvice",
  "menopause support": "menopauseSupport",
};

const ICON_BY_SERVICE_KEY: Record<EapServiceKey, string> = {
  mentalWellbeing: "brain",
  financialAssistance: "coin",
  lifeCoaching: "compass",
  careerCounselling: "briefcase",
  consumerAdviser: "cart",
  mediation: "handshake",
  nonNationals: "globe",
  legalAssistance: "scale",
  parentalAdvice: "parent",
  menopauseSupport: "flower",
};

const FALLBACK_ICON = "heart";

const serviceKeyFor = ({
  img_url: imageUrl,
  title,
}: EapServiceItem): EapServiceKey | null => {
  const imageSlug =
    typeof imageUrl === "string"
      ? imageUrl
          .split("/")
          .pop()
          ?.replace(/\.svg$/, "")
      : undefined;
  if (imageSlug && SERVICE_KEY_BY_IMAGE_SLUG[imageSlug]) {
    return SERVICE_KEY_BY_IMAGE_SLUG[imageSlug];
  }
  const normalisedTitle =
    typeof title === "string" ? title.trim().toLowerCase() : "";
  return SERVICE_KEY_BY_TITLE[normalisedTitle] ?? null;
};

export const selectEnabledEapServices = (
  servicesOffered: EapServicesOffered | null | undefined,
): EapService[] => {
  if (!servicesOffered || !Array.isArray(servicesOffered.items)) return [];
  if (
    servicesOffered.active !== undefined &&
    !isAffirmative(servicesOffered.active)
  )
    return [];

  return (servicesOffered.items as EapServiceItem[])
    .filter(
      (item) =>
        item && isAffirmative(item.active) && typeof item.title === "string",
    )
    .map((item) => {
      const key = serviceKeyFor(item);
      return {
        key,
        title: (item.title as string).trim(),
        icon: key ? ICON_BY_SERVICE_KEY[key] : FALLBACK_ICON,
      };
    });
};
